/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect } from 'react'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PropTypes from 'prop-types'
import { Box, Grid } from 'core/components/Core'
import Paper from 'core/components/Paper'
import Typography from 'core/components/Typography'
import ProductUploadContent from './components/ProductUploadContent'
import ProductUploadFooter from './components/ProductUploadFooter'
import ProductUploadTitle from './components/ProductUploadTitle'
import { actionsUpload, useUpload } from './context'

function ProductUpload({
  selectedImages,
  onSelectConfirm,
  onUploadComplete,
  onUpdateFiles,
  closeModal,
  error,
  errorMessage,
  children,
  hasLabels,
  hasImageWithBadResolution,
  hasWarning,
  checkDependencies,
  fullWidth,
  setProgressState,
  ...rest
}) {
  const [stateUpload, dispatchUpload] = useUpload()

  const onConfirmSelect = () => {
    const filesSelected = stateUpload.FILES.filter(file => file.selected)
    onSelectConfirm(filesSelected)
  }

  useEffect(() => {
    if (setProgressState) {
      setProgressState(stateUpload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateUpload])

  useEffect(() => {
    if (stateUpload.UPLOAD_STATUS === 'COMPLETED') onUploadComplete(stateUpload.FILES)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateUpload.UPLOAD_STATUS])

  useEffect(() => {
    onUpdateFiles(stateUpload.FILES)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateUpload.FILES])

  useEffect(() => {
    // reset if exist cover or selected
    const existSelectOrCover = stateUpload.FILES.some(file => file.selected || file.cover)
    if (existSelectOrCover && selectedImages) {
      const files = stateUpload.FILES.map(file => ({ ...file, selected: false, cover: false }))
      dispatchUpload({ type: actionsUpload.REPLACE_FILES, state: files })
    }

    if (selectedImages && stateUpload.IS_SELECTABLE) {
      // get files - set cover and selected
      const files = stateUpload.FILES.map(file => {
        const isSelect = selectedImages.some(selectedImg => selectedImg.url === file.url)
        const isCover = selectedImages.findIndex(selectedImg => selectedImg.url === file.url) === 0
        if (isSelect) return { ...file, selected: isSelect, cover: isCover }
        return { ...file, selected: false, cover: false }
      })

      // reorder
      const findCover = files.find(file => file.cover === true)
      if (findCover) {
        const findIndex = files.findIndex(file => file.id === findCover.id)
        files.unshift(files.splice(findIndex, 1)[0])
      }
      dispatchUpload({ type: actionsUpload.REPLACE_FILES, state: files })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImages])

  const isSelectionEnabled =
    selectedImages && stateUpload.FILES.some(file => file.selected) && stateUpload.IS_SELECTABLE

  const maxSelectableItems = stateUpload.IS_SELECTABLE ? String(stateUpload.MAX_SELECT_FILES) : ''

  const renderProductUploadLabels = () => {
    if (!hasLabels) {
      return null
    }

    return (
      <Grid item xs={12} sm={12} md={4}>
        <ProductUploadTitle maxSelectableItems={maxSelectableItems} />
      </Grid>
    )
  }

  const renderImageResolutionNotice = () => {
    if (hasImageWithBadResolution) {
      return (
        <div className="d-flex mt-3">
          <InfoOutlinedIcon
            color="primary"
            style={{ fontSize: 13, marginRight: 4, marginTop: 2 }}
          />
          <Typography variant="body2" fontWeight="regular">
            Você ainda tem imagens que não atingiram a resolução mínima para conseguir todos os
            pontos.
          </Typography>
        </div>
      )
    }

    return null
  }

  return (
    <Paper {...rest}>
      <Box pt={4} px={4}>
        <Grid container>
          {renderProductUploadLabels()}
          <Grid item xs={12} sm={12} md={fullWidth ? 12 : 8}>
            <ProductUploadContent
              error={error}
              errorMessage={errorMessage}
              hasLabels={hasLabels}
              hasWarning={hasWarning}
              checkDependencies={checkDependencies}
            />
          </Grid>
          {renderImageResolutionNotice()}
        </Grid>
      </Box>
      {children}
      <ProductUploadFooter
        isPossibleSelect={stateUpload.IS_SELECTABLE}
        isSelectionEnabled={isSelectionEnabled}
        closeModal={closeModal}
        onConfirmSelect={onConfirmSelect}
      />
    </Paper>
  )
}

ProductUpload.defaultProps = {
  onSelectConfirm: null,
  selectedImages: [],
  closeModal: null,
  children: null,
  onUpdateFiles: () => null,
  errorMessage: '',
  error: false,
  hasLabels: false,
  hasImageWithBadResolution: false,
  hasWarning: false,
  checkDependencies: false,
  fullWidth: false,
}

ProductUpload.propTypes = {
  onSelectConfirm: PropTypes.func,
  selectedImages: PropTypes.arrayOf(PropTypes.object),
  onUploadComplete: PropTypes.func.isRequired,
  onUpdateFiles: PropTypes.func,
  closeModal: PropTypes.func,
  children: PropTypes.any,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  hasLabels: PropTypes.bool,
  hasImageWithBadResolution: PropTypes.bool,
  hasWarning: PropTypes.bool,
  checkDependencies: PropTypes.bool,
  fullWidth: PropTypes.bool,
}

export default memo(ProductUpload)
